---
title: "Connect Google Calendar"
description: "Connect Google Calendar so the AI scheduler can see your free time and create interview events. Without it, scheduling interviews is a manual job."
category: "getting-started"
keywords:
  - "Google Calendar ATS integration"
  - "interview scheduling calendar"
  - "connect calendar recruiting"
  - "calendar availability recruiting"
schema: "HowTo"
nav:
  section: "getting-started"
  order: 30
  parent: "/getting-started"
---

Connect the calendar you actually use, and the AI scheduler stops guessing about your availability. This page is how to connect it and what Chosen can see.

## What it unlocks

- **Availability detection** — the scheduler knows which times you're free without you telling it.
- **Event creation** — once a candidate picks a slot, Chosen puts the interview on everyone's calendar and sends the invite.
- **Decline detection** — if a candidate declines the calendar invite, the [AI events tab](/guides/scheduling) tells you before the meeting time arrives, not after.

## How to connect

<Steps>
  <li>Go to **Settings → Integrations**.</li>
  <li>Click **Connect** next to Google Calendar.</li>
  <li>Sign in with Google and grant access.</li>
</Steps>

## It reads free/busy, not your life

Chosen sees *when* you're busy — not *what* you're busy with. Event titles, attendees, and descriptions stay private. A blocked hour is just a blocked hour as far as the scheduler is concerned.

## Every interviewer connects their own

The scheduler can only work around calendars it can see. If you run interview loops with four engineers, all four should connect their calendars — otherwise the scheduler is solving the puzzle with half the pieces and will happily book over someone's focus time.

<Callout type="tip" title="Tentative events still count as busy">
The scheduler treats a "maybe" event as a real one. If you keep placeholder holds on your calendar, the scheduler will avoid them — which is usually what you want, but worth knowing if you wonder why a slot you thought was free never gets offered.
</Callout>

## Without a calendar connected

Scheduling still works — you just do the puzzle yourself. You pick a time, Chosen sends the email, and the [AI events tab](/guides/scheduling) still handles reschedules and follow-ups. It is meaningfully more tedious. If you do more than a couple of interviews a week, connect the calendar.
